import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { user, loginEmail, loginEmail2fa, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user?.role === "admin") navigate("/studio", { replace: true });
  }, [user, loading, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await loginEmail(email, pw);
      if (u?.needs_2fa) {
        setChallengeId(u.challenge_id);
        toast.info("Enter the code from your authenticator app.");
        return;
      }
      if (u.role !== "admin") {
        toast.error("This account does not have admin access.");
        return;
      }
      toast.success("Welcome back.");
      navigate("/studio", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const onSubmit2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await loginEmail2fa(challengeId, code.trim());
      if (u.role !== "admin") {
        toast.error("This account does not have admin access.");
        return;
      }
      toast.success("Welcome back.");
      navigate("/studio", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid authentication code");
      // If the challenge expired, fall back to the password step.
      const msg = String(err?.response?.data?.detail || "");
      if (msg.toLowerCase().includes("expired") || msg.toLowerCase().includes("invalid")) {
        if (msg.toLowerCase().includes("challenge")) {
          setChallengeId(null);
          setCode("");
          setPw("");
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel2fa = () => {
    setChallengeId(null);
    setCode("");
    setPw("");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory px-6">
      <Helmet>
        <title>Studio — Abbot Asia</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="w-full max-w-md">
        <p className="eyebrow">Abbot Asia</p>
        <h1 className="mt-3 font-serif text-4xl text-jade font-light">
          {challengeId ? "Two-factor code" : "Studio sign in"}
        </h1>
        <p className="mt-3 text-ink/65 text-[14px] font-light">
          {challengeId
            ? "Open your authenticator app and enter the current 6-digit code, or use a recovery code (XXXX-XXXX)."
            : "Restricted area. Use the credentials provided to you."}
        </p>

        {!challengeId ? (
          <form onSubmit={onSubmit} className="mt-10 space-y-6">
            <div>
              <label className="eyebrow">Email</label>
              <input
                data-testid="admin-login-email"
                type="email"
                required
                className="input-line mt-3"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="eyebrow">Password</label>
              <input
                data-testid="admin-login-password"
                type="password"
                required
                className="input-line mt-3"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
              />
            </div>
            <button
              data-testid="admin-login-submit"
              disabled={busy}
              type="submit"
              className="btn-primary w-full disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
        ) : (
          <form onSubmit={onSubmit2fa} className="mt-10 space-y-6">
            <div>
              <label className="eyebrow">Authentication code</label>
              <input
                data-testid="admin-login-2fa-code"
                type="text"
                required
                inputMode="text"
                autoComplete="one-time-code"
                placeholder="123 456"
                className="input-line mt-3 tracking-[0.3em] text-center text-lg"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
              />
              <p className="mt-2 text-[12px] text-ink/50">
                6-digit code from Google Authenticator / Microsoft Authenticator / 1Password, or a recovery code.
              </p>
            </div>
            <button
              data-testid="admin-login-2fa-submit"
              disabled={busy || !code.trim()}
              type="submit"
              className="btn-primary w-full disabled:opacity-50"
            >
              {busy ? "Verifying…" : "Verify and sign in"}
            </button>
            <button
              data-testid="admin-login-2fa-cancel"
              type="button"
              onClick={cancel2fa}
              className="block w-full text-center text-[12px] tracking-[0.18em] uppercase text-ink/55 hover:text-jade"
            >
              Cancel and start again
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
